import { useCallback, useEffect, useState } from 'react';

import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';

import type { TimelineNewItemMetadata } from '../types';
import { ItemType } from '../types';

type OptionalTime = number | null;

interface TimelineProps {
  itemTime: OptionalTime;
  isAutoplaying: boolean;
  isDone: boolean;
  onAutoplayToggle: (checked: boolean) => void;
  pause: (showAnimation?: boolean, isManual?: boolean) => void;
  setItemMetadataTime: (itemTime: OptionalTime) => void;
  setCurrentNewItemMetadata: (metadata: TimelineNewItemMetadata) => void;
}

interface TimelineHook {
  cancelAutoplay: () => void;
  showNewItemNow: () => void;
}

export function useTimeline({
  itemTime,
  isAutoplaying,
  isDone,
  onAutoplayToggle,
  pause,
  setItemMetadataTime,
  setCurrentNewItemMetadata,
}: TimelineProps): TimelineHook {
  const [currentItemTime, setCurrentItemTime] = useState<OptionalTime>(itemTime);

  const cancelAutoplay = useCallback(() => {
    if (isDone && isAutoplaying) {
      onAutoplayToggle(false);
    }
  }, [onAutoplayToggle, isDone, isAutoplaying]);

  const showNewItemNow = useCallback(() => {
    const { currentTime } = useRealTimeStore.getState();

    setCurrentNewItemMetadata({ type: ItemType.HIGHLIGHT, time: currentTime });
    cancelAutoplay();
    pause();
  }, [setCurrentNewItemMetadata, cancelAutoplay, pause]);

  useEffect(() => {
    if (itemTime !== currentItemTime) {
      cancelAutoplay();
      setCurrentItemTime(itemTime);
      setItemMetadataTime(itemTime);
    }
  }, [itemTime, currentItemTime, setItemMetadataTime, cancelAutoplay]);

  return {
    cancelAutoplay,
    showNewItemNow,
  };
}
